// ========================================
// KEY MANAGEMENT TAB
// Displays LiteLLM virtual keys for a team with CRUD operations
// ========================================

import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { listTeamKeys, deleteKey, blockKey, unblockKey } from '../services/api';
import Button from './shared/Button';
import StatusBadge from './shared/StatusBadge';
import ConfirmModal from './shared/ConfirmModal';
import KeyCreateEditModal from './KeyCreateEditModal';
import KeyInfoModal from './KeyInfoModal';
import { canDeleteTeams } from '../utils/permissions';

/**
 * Mask a key value for safe display - shows only last 4 chars
 * e.g. "sk-abc123def456" → "sk-...3456"
 */
const maskKey = (keyName) => {
    if (!keyName) return 'sk-...????';
    const last4 = keyName.slice(-4);
    return `sk-...${last4}`;
};

/**
 * Format a budget value for display
 */
const formatBudget = (spend, maxBudget) => {
    if (maxBudget == null || maxBudget === 0) return 'Unlimited';
    const spendStr = `$${(spend || 0).toFixed(2)}`;
    const maxStr = `$${maxBudget.toFixed(2)}`;
    return `${spendStr} / ${maxStr}`;
};

/**
 * Format an expiry date/string for display
 */
const formatExpiry = (expires) => {
    if (!expires) return 'Never';
    try {
        const date = new Date(expires);
        if (isNaN(date.getTime())) return expires;
        return date.toLocaleDateString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    } catch {
        return expires;
    }
};

/**
 * Format models list for display
 */
const formatModels = (models) => {
    if (!models || models.length === 0) return 'All models';
    if (models.length === 1) return models[0];
    if (models.length <= 2) return models.join(', ');
    return `${models[0]}, +${models.length - 1} more`;
};

/**
 * KeyManagementTab Component
 *
 * @param {Object} team - The LiteLLM team object (must have team_id, team_alias)
 * @param {Array} allModels - All available models for key creation
 * @param {Array} allTeams - All teams for resolving team_id to team name
 * @param {Array} allUsers - All users for resolving user_id to email
 * @param {Array} visibleTeams - All visible teams for the team selector dropdown
 * @param {Function} onTeamChange - Callback when team selection changes
 * @param {Object} NO_TEAM_OPTION - The "No Team" option object
 * @param {Function} fmt2 - Currency formatting function
 * @param {Function} fmtDuration - Duration formatting function
 */
const KeyManagementTab = ({ team, allModels = [], allTeams = [], allUsers = [], visibleTeams = [], onTeamChange, NO_TEAM_OPTION, fmt2, fmtDuration, onRefresh }) => {
    const { accounts } = useMsal();
    const currentUserEmail = accounts[0]?.username || accounts[0]?.email || 'unknown@aware.co.th';
    const userCanDelete = canDeleteTeams(currentUserEmail);

    const teamId = team?.team_id;

    // ========================================
    // STATE
    // ========================================
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [initialLoadDone, setInitialLoadDone] = useState(false);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingKey, setEditingKey] = useState(null);
    const [viewingKey, setViewingKey] = useState(null);
    const [newlyCreatedKey, setNewlyCreatedKey] = useState(null); // full key shown once after creation

    const [confirmModal, setConfirmModal] = useState({
        isOpen: false, title: '', message: '', details: '', variant: 'danger', onConfirm: null
    });
    const [errorModal, setErrorModal] = useState({
        isOpen: false, title: '', message: '', details: ''
    });
    const [unblockKeyModal, setUnblockKeyModal] = useState({
        isOpen: false, keyObj: null, inputKey: ''
    });
    const [actionLoading, setActionLoading] = useState(null); // key value being acted on

    const PAGE_SIZE = 20;

    // ========================================
    // DATA FETCHING
    // ========================================
    const fetchKeys = useCallback(async (page = 1) => {
        if (!teamId) return;
        setLoading(true);
        setError(null);

        // Special handling for "No Team" option - pass null to get unassigned keys
        const actualTeamId = teamId === '__no_team__' ? null : teamId;
        const result = await listTeamKeys(actualTeamId, page, PAGE_SIZE);

        if (result.success) {
            const data = result.data;
            const rawKeys = data.keys || [];
            // return_full_object=true gives us objects; filter out any plain strings
            let keyObjects = rawKeys.filter(k => typeof k === 'object' && k !== null);

            // If viewing "No Team" option, filter to only show keys without team_id
            if (teamId === '__no_team__') {
                keyObjects = keyObjects.filter(k => !k.team_id || k.team_id === null);
            }

            setKeys(keyObjects);
            setTotalPages(data.total_pages || 1);
            setTotalCount(keyObjects.length);
            setCurrentPage(data.current_page || page);
        } else {
            setError(result.error || 'Failed to load keys');
        }

        setLoading(false);
        setInitialLoadDone(true);
    }, [teamId]);

    useEffect(() => {
        fetchKeys(1);
    }, [fetchKeys]);

    // ========================================
    // ACTIONS
    // ========================================
    const handleBlockToggle = (keyObj) => {
        const isBlocked = keyObj.blocked;

        // For unblocking, show input modal to get the full key
        if (isBlocked) {
            setUnblockKeyModal({
                isOpen: true,
                keyObj: keyObj,
                inputKey: ''
            });
            return;
        }

        // For blocking, show normal confirmation
        const alias = keyObj.key_alias || maskKey(keyObj.key_name);
        setConfirmModal({
            isOpen: true,
            title: 'Block Key',
            message: `Block "${alias}"?`,
            details: 'The key will be prevented from making any requests.',
            variant: 'danger',
            onConfirm: () => executeBlockToggle(keyObj, false)
        });
    };

    const executeBlockToggle = async (keyObj, isCurrentlyBlocked, providedKey = null) => {
        // For display/tracking, prefer token; for API calls, use key_name
        const displayKey = keyObj.token || keyObj.key_name;
        setActionLoading(displayKey);
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        setUnblockKeyModal(prev => ({ ...prev, isOpen: false }));

        const fn = isCurrentlyBlocked ? unblockKey : blockKey;
        // Pass null for "No Team" option, otherwise pass the actual teamId
        const actualTeamId = teamId === '__no_team__' ? null : teamId;

        // For unblock: use the provided key from user input
        // For block: use the token hash (works with either)
        const keyValue = isCurrentlyBlocked ? providedKey : (keyObj.token || keyObj.key_name);
        const result = await fn(keyValue, actualTeamId, currentUserEmail);

        if (result.success) {
            await fetchKeys(currentPage);
            // Refresh the main dashboard teams data to update the Blocked Keys widget
            if (onRefresh) {
                onRefresh();
            }
        } else {
            const action = isCurrentlyBlocked ? 'unblock' : 'block';
            const errorMessage = typeof result.error === 'string'
                ? result.error
                : JSON.stringify(result.error);
            setErrorModal({
                isOpen: true,
                title: `Failed to ${action} key`,
                message: `Unable to ${action} the key "${keyObj.key_alias || maskKey(keyObj.key_name)}".`,
                details: errorMessage
            });
        }

        setActionLoading(null);
    };

    const handleDelete = (keyObj) => {
        const alias = keyObj.key_alias || maskKey(keyObj.key_name);

        setConfirmModal({
            isOpen: true,
            title: 'Delete Key',
            message: `Permanently delete "${alias}"?`,
            details: 'This cannot be undone. Any applications using this key will immediately stop working.',
            variant: 'danger',
            onConfirm: () => executeDelete(keyObj)
        });
    };

    const executeDelete = async (keyObj) => {
        const keyValue = keyObj.token || keyObj.key_name;
        setActionLoading(keyValue);
        setConfirmModal(prev => ({ ...prev, isOpen: false }));

        // Pass null for "No Team" option, otherwise pass the actual teamId
        const actualTeamId = teamId === '__no_team__' ? null : teamId;
        const result = await deleteKey([keyValue], actualTeamId, currentUserEmail);

        if (result.success) {
            await fetchKeys(currentPage);
        } else {
            const errorMessage = typeof result.error === 'string'
                ? result.error
                : JSON.stringify(result.error);
            setErrorModal({
                isOpen: true,
                title: 'Failed to delete key',
                message: `Unable to delete the key "${keyObj.key_alias || maskKey(keyObj.key_name)}".`,
                details: errorMessage
            });
        }

        setActionLoading(null);
    };

    const handleCreateSuccess = (createdKeyData) => {
        setShowCreateModal(false);
        // Show the full key once - this is the only time it's visible
        if (createdKeyData?.key) {
            setNewlyCreatedKey(createdKeyData);
        }
        fetchKeys(currentPage);
    };

    const handleEditSuccess = () => {
        setEditingKey(null);
        fetchKeys(currentPage);
    };

    // ========================================
    // RENDER
    // ========================================
    const styles = {
        container: { padding: '0' },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
        },
        countLabel: { color: 'var(--muted)', fontSize: '0.9rem' },
        errorBox: {
            padding: '1rem',
            backgroundColor: 'var(--critical-bg)',
            border: '1px solid var(--critical)',
            borderRadius: '6px',
            color: 'var(--critical)',
            fontSize: '0.9rem',
            marginBottom: '1rem'
        },
        loadingBox: {
            padding: '2rem',
            textAlign: 'center',
            color: 'var(--muted)'
        },
        emptyBox: {
            padding: '2rem',
            textAlign: 'center',
            color: 'var(--muted)',
            fontStyle: 'italic',
            backgroundColor: 'var(--surface)',
            borderRadius: '8px'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.85rem'
        },
        th: {
            textAlign: 'left',
            padding: '0.6rem 0.75rem',
            backgroundColor: 'var(--surface)',
            color: 'var(--muted)',
            fontWeight: '600',
            fontSize: '0.8rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottom: '1px solid var(--border)'
        },
        td: {
            padding: '0.6rem 0.75rem',
            borderBottom: '1px solid var(--border)',
            color: 'var(--fg-primary)',
            verticalAlign: 'middle'
        },
        keyMask: {
            fontFamily: 'monospace',
            color: 'var(--muted)',
            fontSize: '0.8rem'
        },
        actions: {
            display: 'flex',
            gap: '0.35rem',
            alignItems: 'center'
        },
        pagination: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '1rem',
            color: 'var(--muted)',
            fontSize: '0.85rem'
        }
    };

    const teamForKeys = team || NO_TEAM_OPTION;

    return (
        <div style={styles.container}>
            {!initialLoadDone ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)', fontSize: '0.95rem' }}>
                    Loading...
                </div>
            ) : (
                <>
                    {/* Team selector - only show when visibleTeams is provided (global view) */}
                    {visibleTeams && visibleTeams.length > 0 && (
                        <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1rem' }}>
                            <label style={{ fontSize: '0.73rem', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem', display: 'block' }}>Select Team</label>
                            <select
                                style={{ padding: '0.45rem 0.65rem', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--fg-primary)', fontSize: '0.9rem', width: '100%', maxWidth: '400px', boxSizing: 'border-box', cursor: 'pointer' }}
                                value={teamForKeys.team_id}
                                onChange={(e) => {
                                    if (e.target.value === '__no_team__') {
                                        onTeamChange && onTeamChange(NO_TEAM_OPTION);
                                    } else {
                                        const selectedTeam = visibleTeams.find(t => t.team_id === e.target.value);
                                        onTeamChange && onTeamChange(selectedTeam);
                                    }
                                }}
                            >
                                <option key="__no_team__" value="__no_team__">
                                    No Team (Unassigned)
                                </option>
                                {visibleTeams.map(t => {
                                    const duration = fmtDuration(t.budget_duration);
                                    return (
                                        <option key={t.team_id} value={t.team_id}>
                                            {t.team_alias || t.team_id} {t.max_budget > 0 ? `(Spend: ${fmt2(t.spend)} Budget Limit: ${fmt2(t.max_budget)} (${duration.toLowerCase()}))` : ''}
                                        </option>
                                    );
                                })}
                            </select>
                            <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--fg-secondary)' }}>
                                {teamForKeys.team_id === '__no_team__' ? (
                                    <>Managing API keys for: <span style={{ color: 'var(--fg-primary)', fontWeight: 600 }}>Unassigned Keys</span> · Keys created without team assignment</>
                                ) : (
                                    <>
                                        Managing API keys for: <span style={{ color: 'var(--fg-primary)', fontWeight: 600 }}>{teamForKeys.team_alias || teamForKeys.team_id}</span>
                                        {teamForKeys.max_budget > 0 && (
                                            <span> · Budget: <span style={{ color: '#10b981' }}>{fmt2(teamForKeys.spend)}</span> / {fmt2(teamForKeys.max_budget)}</span>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Header */}
                    <div style={styles.header}>
                        <span style={styles.countLabel}>
                            {team?.team_id === '__no_team__'
                                ? `${totalCount} unassigned key${totalCount !== 1 ? 's' : ''}`
                                : `${totalCount} key${totalCount !== 1 ? 's' : ''} for this team`
                            }
                        </span>
                        <Button variant="success" size="small" onClick={() => setShowCreateModal(true)}>
                            + Create Key
                        </Button>
                    </div>

                    {error && <div style={styles.errorBox}>{error}</div>}

                    {keys.length === 0 ? (
                <div style={styles.emptyBox}>
                    {team?.team_id === '__no_team__'
                        ? 'No unassigned keys found. Create one to get started.'
                        : 'No keys found for this team. Create one to get started.'
                    }
                </div>
            ) : (
                <>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Alias</th>
                                    <th style={styles.th}>Key</th>
                                    <th style={styles.th}>Models</th>
                                    <th style={styles.th}>Budget (spent/max)</th>
                                    <th style={styles.th}>Expires</th>
                                    <th style={styles.th}>Status</th>
                                    <th style={styles.th}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {keys.map((keyObj, idx) => {
                                    const keyValue = keyObj.token || keyObj.key_name;
                                    const isActing = actionLoading === keyValue;
                                    const rowBg = idx % 2 === 0 ? 'var(--surface)' : 'var(--surface-alt)';
                                    return (
                                        <tr key={keyValue || idx} style={{ backgroundColor: rowBg }}>
                                            <td style={styles.td}>
                                                {keyObj.key_alias ? (
                                                    <span style={{ color: 'var(--fg-primary)', fontWeight: '500' }}>
                                                        {keyObj.key_alias}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>
                                                        Unnamed
                                                    </span>
                                                )}
                                            </td>
                                            <td style={styles.td}>
                                                <span style={styles.keyMask}>
                                                    {maskKey(keyObj.key_name)}
                                                </span>
                                            </td>
                                            <td style={styles.td}>
                                                <span style={{ color: 'var(--fg-secondary)', fontSize: '0.8rem' }}
                                                    title={(keyObj.models || []).join(', ') || 'All models'}>
                                                    {formatModels(keyObj.models)}
                                                </span>
                                            </td>
                                            <td style={styles.td}>
                                                <span style={{ color: 'var(--fg-secondary)' }}>
                                                    {formatBudget(keyObj.spend, keyObj.max_budget)}
                                                </span>
                                            </td>
                                            <td style={styles.td}>
                                                <span style={{ color: 'var(--fg-secondary)', fontSize: '0.8rem' }}>
                                                    {formatExpiry(keyObj.expires)}
                                                </span>
                                            </td>
                                            <td style={styles.td}>
                                                <StatusBadge
                                                    label={keyObj.blocked ? 'Blocked' : 'Active'}
                                                    variant={keyObj.blocked ? 'unhealthy' : 'healthy'}
                                                    size="small"
                                                />
                                            </td>
                                            <td style={styles.td}>
                                                <div style={styles.actions}>
                                                    <Button
                                                        variant="secondary"
                                                        size="small"
                                                        onClick={() => setViewingKey(keyObj)}
                                                        disabled={isActing}
                                                    >
                                                        Info
                                                    </Button>
                                                    <Button
                                                        variant="primary"
                                                        size="small"
                                                        onClick={() => setEditingKey(keyObj)}
                                                        disabled={isActing}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        variant={keyObj.blocked ? 'success' : 'secondary'}
                                                        size="small"
                                                        onClick={() => handleBlockToggle(keyObj)}
                                                        loading={isActing}
                                                        disabled={isActing}
                                                    >
                                                        {keyObj.blocked ? 'Unblock' : 'Block'}
                                                    </Button>
                                                    {/* Delete button - only for admins */}
                                                    {userCanDelete && (
                                                        <Button
                                                            variant="danger"
                                                            size="small"
                                                            onClick={() => handleDelete(keyObj)}
                                                            loading={isActing}
                                                            disabled={isActing}
                                                        >
                                                            Delete
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div style={styles.pagination}>
                            <span>Page {currentPage} of {totalPages}</span>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <Button
                                    variant="secondary"
                                    size="small"
                                    onClick={() => fetchKeys(currentPage - 1)}
                                    disabled={currentPage <= 1 || loading}
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="small"
                                    onClick={() => fetchKeys(currentPage + 1)}
                                    disabled={currentPage >= totalPages || loading}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
                </>
            )}

            {/* Create Key Modal */}
            {showCreateModal && (
                <KeyCreateEditModal
                    mode="create"
                    team={team}
                    allTeams={allTeams}
                    allModels={allModels}
                    onSuccess={handleCreateSuccess}
                    onClose={() => setShowCreateModal(false)}
                />
            )}

            {/* Edit Key Modal */}
            {editingKey && (
                <KeyCreateEditModal
                    mode="edit"
                    keyData={editingKey}
                    team={team}
                    allTeams={allTeams}
                    allModels={allModels}
                    onSuccess={handleEditSuccess}
                    onClose={() => setEditingKey(null)}
                />
            )}

            {/* Key Info Modal */}
            {viewingKey && (
                <KeyInfoModal
                    keyData={viewingKey}
                    onClose={() => setViewingKey(null)}
                    allTeams={allTeams}
                    allUsers={allUsers}
                />
            )}

            {/* Newly Created Key - one-time display */}
            {newlyCreatedKey && (
                <NewKeyRevealModal
                    keyData={newlyCreatedKey}
                    onClose={() => setNewlyCreatedKey(null)}
                />
            )}

            {/* Confirm Modal */}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                details={confirmModal.details}
                variant={confirmModal.variant}
                loading={!!actionLoading}
            />

            {/* Error Modal */}
            <ConfirmModal
                isOpen={errorModal.isOpen}
                onClose={() => setErrorModal(prev => ({ ...prev, isOpen: false }))}
                onConfirm={() => setErrorModal(prev => ({ ...prev, isOpen: false }))}
                title={errorModal.title}
                message={errorModal.message}
                details={errorModal.details}
                variant="danger"
                confirmLabel="OK"
            />

            {/* Unblock Key Modal - requires user to provide the full key */}
            {unblockKeyModal.isOpen && (
                <UnblockKeyModal
                    keyObj={unblockKeyModal.keyObj}
                    onClose={() => setUnblockKeyModal({ isOpen: false, keyObj: null, inputKey: '' })}
                    onConfirm={(providedKey) => executeBlockToggle(unblockKeyModal.keyObj, true, providedKey)}
                    loading={!!actionLoading}
                />
            )}
        </div>
    );
};

// ========================================
// UNBLOCK KEY MODAL
// Prompts user to enter the full unhashed key to unblock it
// ========================================
const UnblockKeyModal = ({ keyObj, onClose, onConfirm, loading }) => {
    const [inputKey, setInputKey] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = () => {
        if (!inputKey || !inputKey.trim()) {
            setError('Please enter the API key');
            return;
        }

        if (!inputKey.startsWith('sk-')) {
            setError('Invalid key format. Keys must start with "sk-"');
            return;
        }

        onConfirm(inputKey.trim());
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !loading) {
            handleSubmit();
        }
    };

    const alias = keyObj?.key_alias || maskKey(keyObj?.key_name);

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'var(--backdrop)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1300, padding: '20px'
        }}>
            <div style={{
                backgroundColor: 'var(--surface)', borderRadius: '12px',
                boxShadow: '0 10px 40px var(--overlay)',
                maxWidth: '560px', width: '100%', padding: '2rem'
            }}>
                <h2 style={{ color: 'var(--signal-blue)', marginTop: 0, marginBottom: '0.5rem', fontSize: '1.3rem' }}>
                    Unblock Key
                </h2>
                <p style={{ color: 'var(--fg-primary)', marginBottom: '1rem', fontSize: '0.95rem', lineHeight: 1.5 }}>
                    To unblock <strong>{alias}</strong>, please enter the full API key value.
                </p>
                <p style={{ color: 'var(--muted)', marginBottom: '1.5rem', fontSize: '0.85rem', lineHeight: 1.4 }}>
                    The full key was only shown when created. If you don't have it, delete this key and create a new one.
                </p>

                <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
                        API Key (starts with sk-)
                    </div>
                    <input
                        type="text"
                        value={inputKey}
                        onChange={(e) => {
                            setInputKey(e.target.value);
                            setError('');
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder="sk-..."
                        disabled={loading}
                        style={{
                            width: '100%',
                            padding: '0.75rem',
                            backgroundColor: 'var(--surface)',
                            border: error ? '1px solid var(--critical)' : '1px solid var(--border)',
                            borderRadius: '6px',
                            color: 'var(--fg-primary)',
                            fontSize: '0.9rem',
                            fontFamily: 'monospace',
                            outline: 'none',
                            boxSizing: 'border-box'
                        }}
                        autoFocus
                    />
                    {error && (
                        <div style={{ color: 'var(--critical)', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                            {error}
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                    <button
                        onClick={onClose}
                        disabled={loading}
                        style={{
                            padding: '0.6rem 1.5rem',
                            backgroundColor: 'var(--border)',
                            color: 'var(--fg-primary)',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontWeight: '500',
                            fontSize: '0.95rem',
                            opacity: loading ? 0.6 : 1
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        style={{
                            padding: '0.6rem 1.5rem',
                            backgroundColor: loading ? 'var(--muted)' : 'var(--signal-blue)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontWeight: '500',
                            fontSize: '0.95rem'
                        }}
                    >
                        {loading ? 'Unblocking...' : 'Unblock Key'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ========================================
// NEW KEY REVEAL MODAL
// Shows the full key value once after creation
// Requires user to type the key to confirm they've saved it
// ========================================
const NewKeyRevealModal = ({ keyData, onClose }) => {
    const [copied, setCopied] = useState(false);
    const [inputKey, setInputKey] = useState('');
    const [error, setError] = useState('');

    const handleCopy = () => {
        navigator.clipboard.writeText(keyData.key).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleConfirm = () => {
        if (inputKey.trim() === keyData.key) {
            onClose();
        } else {
            setError('The key you entered does not match. Please copy and paste the key exactly.');
        }
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'var(--backdrop)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1300, padding: '20px'
        }}>
            <div style={{
                backgroundColor: 'var(--surface)', borderRadius: '12px',
                boxShadow: '0 10px 40px var(--overlay)',
                maxWidth: '560px', width: '100%', padding: '2rem'
            }}>
                <h2 style={{ color: 'var(--success)', marginTop: 0, marginBottom: '0.5rem', fontSize: '1.3rem' }}>
                    Key Created Successfully
                </h2>
                <p style={{ color: 'var(--critical)', marginBottom: '1.5rem', fontSize: '0.9rem', lineHeight: 1.5 }}>
                    <strong>Copy this key now.</strong> For security reasons, the full key value
                    will not be shown again after you close this dialog.
                </p>

                {keyData.key_alias && (
                    <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Alias</div>
                        <div style={{ color: 'var(--fg-primary)', fontWeight: '500' }}>{keyData.key_alias}</div>
                    </div>
                )}

                <div style={{ marginBottom: '1rem' }}>
                    <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>API Key</div>
                    <div style={{
                        backgroundColor: 'var(--surface)',
                        border: '1px solid var(--border)',
                        borderRadius: '6px',
                        padding: '0.75rem 1rem',
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        color: 'var(--success)',
                        wordBreak: 'break-all',
                        userSelect: 'all'
                    }}>
                        {keyData.key}
                    </div>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '0.85rem', color: 'var(--warning)', marginBottom: '0.75rem', fontWeight: '500' }}>
                        To confirm you have saved the key, please type or paste it below:
                    </div>
                    <input
                        type="text"
                        value={inputKey}
                        onChange={(e) => {
                            setInputKey(e.target.value);
                            setError('');
                        }}
                        placeholder="Paste the key here to confirm..."
                        style={{
                            width: '100%',
                            padding: '0.75rem 1rem',
                            borderRadius: '6px',
                            border: error ? '1px solid var(--critical)' : '1px solid var(--border)',
                            backgroundColor: 'var(--surface)',
                            color: 'var(--fg-primary)',
                            fontSize: '0.9rem',
                            fontFamily: 'monospace',
                            outline: 'none',
                            boxSizing: 'border-box'
                        }}
                    />
                    {error && (
                        <div style={{ color: 'var(--critical)', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                            {error}
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                    <button
                        onClick={handleCopy}
                        style={{
                            padding: '0.6rem 1.5rem',
                            backgroundColor: copied ? 'var(--success)' : 'var(--border)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            fontSize: '0.95rem'
                        }}
                    >
                        {copied ? 'Copied!' : 'Copy Key'}
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!inputKey.trim()}
                        style={{
                            padding: '0.6rem 1.5rem',
                            backgroundColor: !inputKey.trim() ? 'var(--muted)' : 'var(--signal-blue)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: !inputKey.trim() ? 'not-allowed' : 'pointer',
                            fontWeight: '500',
                            fontSize: '0.95rem',
                            opacity: !inputKey.trim() ? 0.6 : 1
                        }}
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
};

export default KeyManagementTab;

